import React, { useEffect, useState, useCallback } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import DataTable from "../../components/DataTable/DataTable";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Checkbox,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { Routes, Route } from "react-router-dom";
import { usePopup } from "../../components/PopupProvider";
import taskTemplateSchema from "./schemas/taskTemplate.json";

const fieldSx = { "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } };

function FormRow({ label, children, alignStart }) {
  return (
    <div
      className={`grid grid-cols-[160px_1fr] gap-2 py-px ${alignStart ? "items-start" : "items-center"}`}
    >
      <span
        className={`text-xs text-gray-600 whitespace-nowrap ${alignStart ? "pt-2" : ""}`}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

export default function TaskTemplates() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const SESSION_PAGE_SIZE_KEY = "compliance_ui_page_size";
  const defaultSize = (() => {
    try {
      const s = sessionStorage.getItem(SESSION_PAGE_SIZE_KEY);
      const n = Number(s);
      if (n) return n;
    } catch (e) {}
    return 20;
  })();
  const [size, setSize] = useState(defaultSize);
  const [total, setTotal] = useState(0);
  const [activeFilters, setActiveFilters] = useState({});
  const [lastLoadFailed, setLastLoadFailed] = useState(false);

  const load = useCallback(
    async (p = page, s = size, filters = activeFilters) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", p);
        params.set("size", s);
        Object.entries(filters || {}).forEach(([k, v]) => {
          params.set(`filter.${k}`, v);
        });
        const data = await apiGet(`/api/task-templates?${params.toString()}`);
        const content = data.content || data || [];
        setList(content);
        setTotal(data.totalElements || content.length || 0);
      } catch (err) {
        console.error(err);
        alert("Failed to load task templates: " + err.message);
      } finally {
        setLoading(false);
      }
    },
    [page, size, activeFilters],
  );

  const handleFiltersChange = React.useCallback(
    (filters) => {
      if (lastLoadFailed) return;
      setActiveFilters((prev) => {
        const prevKeys = Object.keys(prev || {});
        const nextKeys = Object.keys(filters || {});
        if (
          prevKeys.length === nextKeys.length &&
          nextKeys.every(
            (k) => String(prev[k] || "") === String(filters[k] || ""),
          )
        ) {
          return prev;
        }
        return filters;
      });
      setPage(0);
      load(0, size, filters || {});
    },
    [lastLoadFailed, size, load],
  );

  useEffect(() => {
    load();
  }, []);

  const handlePageSizeChange = React.useCallback(
    (newSize) => {
      try {
        sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, String(newSize));
      } catch (e) {}
      setSize(newSize);
      setPage(0);
    },
    [load, activeFilters],
  );

  const handlePageChange = React.useCallback(
    (newPage) => {
      setPage(newPage);
    },
    [load, size, activeFilters],
  );

  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);
  const [newTaskName, setNewTaskName] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [newTaskFrequency, setNewTaskFrequency] = useState("");
  const [newTaskEnable, setNewTaskEnable] = useState(true);
  const [newTaskFiscalMonth, setNewTaskFiscalMonth] = useState(4);
  const [editingItem, setEditingItem] = useState(null);
  const { showConfirm } = usePopup();

  async function handleSave() {
    if (!newTaskName) {
      alert("Please enter a name for the Task Template.");
      return;
    }
    try {
      const payload = {
        name: newTaskName,
        description: newTaskDescription,
        frequency: newTaskFrequency || null,
        enable: newTaskEnable,
        fiscalYearStartMonth: newTaskFiscalMonth,
      };
      if (editingItem) {
        await apiPut(`/api/task-templates/${editingItem.id}`, payload);
        alert("Task Template updated.");
      } else {
        await apiPost("/api/task-templates", payload);
        alert("Task Template created successfully.");
      }
      setShowNewTaskDialog(false);
      setEditingItem(null);
      setNewTaskName("");
      setNewTaskDescription("");
      setNewTaskFrequency("");
      setNewTaskEnable(true);
      setNewTaskFiscalMonth(4);
      load();
    } catch (err) {
      alert("Save failed: " + err.message);
    }
  }

  function openAddDialog() {
    setEditingItem(null);
    setNewTaskName("");
    setNewTaskDescription("");
    setNewTaskFrequency("");
    setNewTaskEnable(true);
    setNewTaskFiscalMonth(4);
    setShowNewTaskDialog(true);
  }

  function openEditDialog(item) {
    setEditingItem(item);
    setNewTaskName(item.name || "");
    setNewTaskDescription(item.description || "");
    setNewTaskFrequency(item.frequency || "");
    setNewTaskEnable(item.enable === undefined ? true : item.enable);
    setNewTaskFiscalMonth(item.fiscalYearStartMonth || 4);
    setShowNewTaskDialog(true);
  }

  async function handleDelete(item) {
    try {
      const ok = await showConfirm(`Delete task template '${item.name}'?`, {
        title: "Delete Task Template",
        confirmText: "Delete",
        cancelText: "Cancel",
        severity: "error",
      });
      if (!ok) return;
      await apiDelete(`/api/task-templates/${item.id}`);
      load();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  }

  return (
    <div className="h-[89.8vh]">
      <Routes>
        <Route
          index
          element={
            <Box sx={{ m: 1 }}>
              <Card>
                <CardContent>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6">Task Templates</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={openAddDialog}
                    >
                      Add Task Template
                    </Button>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <DataTable
                      schema={taskTemplateSchema}
                      rows={list}
                      onFiltersChange={handleFiltersChange}
                      onPageSizeChange={handlePageSizeChange}
                      totalCount={total}
                      currentPage={page}
                      onPageChange={handlePageChange}
                      extraControls={null}
                      onEdit={(_, row) => openEditDialog(row)}
                      onDelete={(_, row) => handleDelete(row)}
                    />
                    {loading ? (
                      <div style={{ padding: 12 }}>Loading...</div>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            </Box>
          }
        />
      </Routes>

      <Dialog
        open={showNewTaskDialog}
        onClose={() => {
          setShowNewTaskDialog(false);
          setEditingItem(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1 }}>
          {editingItem ? "Edit Task Template" : "Create Task Template"}
        </DialogTitle>
        <DialogContent dividers>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              padding: "4px 0",
            }}
          >
            <FormRow label="Name">
              <TextField
                size="small"
                margin="dense"
                fullWidth
                sx={fieldSx}
                value={newTaskName}
                onChange={(e) => setNewTaskName(e.target.value)}
                autoFocus
              />
            </FormRow>
            <FormRow label="Description" alignStart>
              <TextField
                size="small"
                margin="dense"
                fullWidth
                multiline
                rows={2}
                sx={fieldSx}
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
              />
            </FormRow>
            <FormRow label="Frequency">
              <TextField
                size="small"
                margin="dense"
                select
                fullWidth
                sx={fieldSx}
                value={newTaskFrequency}
                onChange={(e) => setNewTaskFrequency(e.target.value)}
              >
                <MenuItem value="DAILY">DAILY</MenuItem>
                <MenuItem value="WEEKLY">WEEKLY</MenuItem>
                <MenuItem value="BIWEEKLY">BIWEEKLY</MenuItem>
                <MenuItem value="MONTHLY">MONTHLY</MenuItem>
                <MenuItem value="QUARTERLY">QUARTERLY</MenuItem>
                <MenuItem value="YEARLY">YEARLY</MenuItem>
              </TextField>
            </FormRow>
            <FormRow label="FY Start Month">
              <TextField
                size="small"
                margin="dense"
                type="number"
                fullWidth
                sx={fieldSx}
                inputProps={{ min: 1, max: 12 }}
                value={newTaskFiscalMonth}
                onChange={(e) =>
                  setNewTaskFiscalMonth(parseInt(e.target.value || "0", 10))
                }
              />
            </FormRow>
            <FormRow label="Enabled">
              <div className="flex items-center h-full">
                <Checkbox
                  size="small"
                  checked={newTaskEnable}
                  onChange={(e) => setNewTaskEnable(e.target.checked)}
                  // sx={{ p: 0.5 }}
                />
              </div>
            </FormRow>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={() => {
              setShowNewTaskDialog(false);
              setEditingItem(null);
            }}
          >
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            {editingItem ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
